/**
 * CommunityRedirect
 *
 * SSO landing page that bridges the merchant dashboard to community.userevstar.com.
 * Renders inside the Shell, so layout flows with the dashboard topbar — no
 * 100vh centering. Mobile-first hero + feature grid + sticky CTA on phones.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, MessageSquare, Lightbulb, Trophy, Handshake, ArrowRight, AlertCircle, Sparkles,
} from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import './CommunityRedirect.css';

const FEATURES = [
  {
    icon: MessageSquare,
    title: 'Discussions',
    body: 'Ask questions and trade hard-won lessons with merchants running stores like yours.',
  },
  {
    icon: Handshake,
    title: 'Suppliers',
    body: 'Connect with verified suppliers and manufacturers — source products, compare quotes and lock in bulk pricing.',
  },
  {
    icon: Lightbulb,
    title: 'Playbooks',
    body: 'Step-by-step guides from operators who have already cracked the channel you are working on.',
  },
  {
    icon: Trophy,
    title: 'Wins',
    body: 'Celebrate milestones, get inspired, and learn what is actually moving revenue this week.',
  },
];

const CommunityRedirect = () => {
  const [status, setStatus] = useState('welcome'); // 'welcome' | 'loading' | 'error'
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleJoinCommunity = async () => {
    setStatus('loading');
    try {
      const response = await axiosInstance.get('/merchants/community/sso');
      if (response.data.success && response.data.redirectUrl) {
        window.location.href = response.data.redirectUrl;
      } else {
        setStatus('error');
        setError('Failed to generate community access token');
      }
    } catch (err) {
      console.error('Community SSO error:', err);
      setStatus('error');
      setError(err.response?.data?.message || 'Failed to connect to community');
    }
  };

  if (status === 'error') {
    return (
      <div className="community-page">
        <div className="community-state">
          <div className="community-state__icon community-state__icon--error" aria-hidden>
            <AlertCircle size={28} />
          </div>
          <h2 className="community-state__title">Connection failed</h2>
          <p className="community-state__body">{error}</p>
          <button
            type="button"
            onClick={() => setStatus('welcome')}
            className="community-btn community-btn--primary"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="community-page">
        <div className="community-state">
          <span className="community-state__spinner" aria-hidden />
          <h2 className="community-state__title">Signing you in</h2>
          <p className="community-state__body">Hang on — generating your community access token…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="community-page">
      <section className="community-hero">
        <div className="community-hero__badge" aria-hidden>
          <Sparkles size={12} />
          <span>Merchant community</span>
        </div>
        <h1 className="community-hero__title">
          Trade notes with merchants running stores like yours.
        </h1>
        <p className="community-hero__sub">
          Discussions, playbooks and win stories from operators across Africa,
          plus direct access to verified suppliers when you are ready to source.
          You will be signed in automatically with your merchant account — no
          new password to remember.
        </p>
        <div className="community-hero__cta-row">
          <button
            type="button"
            onClick={handleJoinCommunity}
            className="community-btn community-btn--primary community-btn--lg"
          >
            Enter community
            <ArrowRight size={16} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="community-link"
          >
            Maybe later
          </button>
        </div>
        <div className="community-hero__trust">
          <span className="community-hero__avatars" aria-hidden>
            <span className="community-hero__avatar" />
            <span className="community-hero__avatar" />
            <span className="community-hero__avatar" />
            <span className="community-hero__avatar community-hero__avatar--more">
              <Users size={11} />
            </span>
          </span>
          <span className="community-hero__trust-text">
            Join the merchants already swapping playbooks every week
          </span>
        </div>
      </section>

      <section className="community-features" aria-label="What is inside the community">
        {FEATURES.map(({ icon: Icon, title, body }) => (
          <article key={title} className="community-feature">
            <span className="community-feature__icon" aria-hidden>
              <Icon size={16} />
            </span>
            <h3 className="community-feature__title">{title}</h3>
            <p className="community-feature__body">{body}</p>
          </article>
        ))}
      </section>
    </div>
  );
};

export default CommunityRedirect;
